import { GoLiveSection } from "./merchant/components/GoLiveSection";
import { MerchantOverview } from "./merchant/components/MerchantOverview";

import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, Routes, Route, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth, API } from "@/App";
import { useTheme } from "@/context/ThemeContext";
import { ThemeToggleCompact } from "@/components/ThemeToggle";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Package,
  FolderOpen,
  FileText,
  ShoppingCart,
  RefreshCw,
  LogOut,
  Menu,
  X,
  Plus,
  Edit,
  Trash2,
  PoundSterling,
  Users,
  TrendingUp,
  ChevronRight,
  AlertCircle,
  Wallet,
  Copy,
  ExternalLink,
  Eye,
  Send,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  Clock,
  Upload,
  CreditCard,
  Receipt,
  Rocket,
  MessageSquare,
  Sparkles,
  CalendarDays,
  Smartphone,
  Lock,
  PartyPopper,
  Crown,
  Zap,
  Building2,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Check,
  Settings,
  History
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

// Predefined category library for merchants
const CATEGORY_LIBRARY = [
  // Retail & Products
  { name: "Electronics", description: "Electronic devices, gadgets, and accessories" },
  { name: "Clothing & Apparel", description: "Fashion, clothing, and wearable items" },
  { name: "Footwear", description: "Shoes, trainers, boots, and sandals" },
  { name: "Accessories", description: "Bags, jewelry, watches, and fashion accessories" },
  { name: "Home & Garden", description: "Home decor, furniture, and garden supplies" },
  { name: "Health & Beauty", description: "Skincare, cosmetics, and wellness products" },
  { name: "Sports & Fitness", description: "Sports equipment and fitness gear" },
  { name: "Toys & Games", description: "Toys, board games, and entertainment" },
  { name: "Books & Stationery", description: "Books, notebooks, and office supplies" },
  { name: "Food & Beverages", description: "Food items, drinks, and groceries" },
  { name: "Pet Supplies", description: "Pet food, accessories, and care products" },
  
  // Services
  { name: "Web Development", description: "Website design and development services" },
  { name: "App Development", description: "Mobile and desktop application development" },
  { name: "Digital Marketing", description: "SEO, social media, and online advertising" },
  { name: "Graphic Design", description: "Logo, branding, and visual design services" },
  { name: "Photography", description: "Photo shoots, editing, and visual content" },
  { name: "Video Production", description: "Video filming, editing, and production" },
  { name: "Consulting", description: "Business and professional consulting services" },
  { name: "Training & Courses", description: "Educational courses and training programs" },
  { name: "Legal Services", description: "Legal advice and documentation services" },
  { name: "Accounting & Finance", description: "Bookkeeping, tax, and financial services" },
  
  // Food & Hospitality
  { name: "Main Courses", description: "Primary dishes and entrees" },
  { name: "Starters & Appetizers", description: "Small plates and appetizers" },
  { name: "Desserts", description: "Sweet treats and desserts" },
  { name: "Drinks & Beverages", description: "Hot and cold beverages" },
  { name: "Sides", description: "Side dishes and accompaniments" },
  { name: "Specials", description: "Daily specials and limited offers" },
  
  // Salon & Beauty
  { name: "Haircuts", description: "Hair cutting and styling services" },
  { name: "Hair Colouring", description: "Hair dye, highlights, and colour treatments" },
  { name: "Nail Services", description: "Manicure, pedicure, and nail art" },
  { name: "Facials & Skincare", description: "Face treatments and skincare services" },
  { name: "Massage & Spa", description: "Relaxation and therapeutic treatments" },
  { name: "Makeup", description: "Professional makeup services" },
  
  // Automotive
  { name: "Car Parts", description: "Vehicle parts and components" },
  { name: "Car Accessories", description: "Vehicle accessories and add-ons" },
  { name: "Car Services", description: "Vehicle maintenance and repair services" },
  
  // Real Estate & Property
  { name: "Property Sales", description: "Properties for sale" },
  { name: "Property Rentals", description: "Properties for rent" },
  { name: "Property Services", description: "Property management and related services" },
  
  // Other
  { name: "Subscriptions", description: "Recurring subscription products or services" },
  { name: "Gift Cards", description: "Gift vouchers and cards" },
  { name: "Memberships", description: "Club and membership packages" },
  { name: "Custom/Other", description: "Other products or services" }
];

const MerchantDashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [penalties, setPenalties] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [platformAgreement, setPlatformAgreement] = useState(null);
  const [merchantSubscription, setMerchantSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);

  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [showSignaturesModal, setShowSignaturesModal] = useState(false);
  const [showSendAgreementModal, setShowSendAgreementModal] = useState(false);
  const [showPlatformAgreementModal, setShowPlatformAgreementModal] = useState(false);
  const [merchantSignature, setMerchantSignature] = useState("");
  const [showPenaltyModal, setShowPenaltyModal] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  // Form data
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    price: "",
    category_id: "",
    images: [],
    stock: 0,
    is_subscription: false,
    subscription_interval: "monthly",
    subscription_price: "",
    direct_debit_enabled: false,
    penalty_percentage: ""
  });

  const [categoryForm, setCategoryForm] = useState({ name: "", description: "" });
  const [agreementForm, setAgreementForm] = useState({ product_id: "", terms: "", penalty_terms: "" });
  const [sendAgreementForm, setSendAgreementForm] = useState({ email: "", name: "" });
  const [penaltyForm, setPenaltyForm] = useState({ 
    amount: "", 
    reason: "", 
    penalty_type: "other",
    proof_document_url: "",
    proof_document_name: "",
    additional_notes: "",
    charge_immediately: false
  });
  const [uploadingProof, setUploadingProof] = useState(false);
  const [uploadingProductImage, setUploadingProductImage] = useState(false);
  const productImageRef = useRef(null);
  
  // Payment flow state - V2 Pricing
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [v2Pricing, setV2Pricing] = useState(null);
  const [featureAccess, setFeatureAccess] = useState({ base: false, crm: false, pos: false, services: false });
  
  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  
  // Subscription history state
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // Payment Setup state
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showPaymentSetupModal, setShowPaymentSetupModal] = useState(false);
  const [settingUpPayment, setSettingUpPayment] = useState(false);
  
  // Order management state
  const [orderFilter, setOrderFilter] = useState("all");
  const [selectedOrderForDetail, setSelectedOrderForDetail] = useState(null);
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [selectedOrderForRefund, setSelectedOrderForRefund] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("requested_by_customer");
  const [processingRefund, setProcessingRefund] = useState(false);
  
  // Customer Billing Settings state
  const [showBillingSettingsModal, setShowBillingSettingsModal] = useState(false);
  const [customerBillingSettings, setCustomerBillingSettings] = useState({
    weekly: { enabled: false, price: "" },
    monthly: { enabled: true, price: "" },
    yearly: { enabled: false, price: "" }
  });
  const [savingBillingSettings, setSavingBillingSettings] = useState(false);
  
  // Trial/Subscription Status
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Fetch subscription status
  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${API}/stripe/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data);
      }
    } catch (error) {
      console.error("Failed to fetch subscription status:", error);
    }
  };

  // Handle Subscribe Now - redirect to Stripe checkout
  const [subscribing, setSubscribing] = useState(false);
  const handleSubscribeNow = async () => {
    setSubscribing(true);
    try {
      const response = await fetch(`${API}/stripe/subscription/create-checkout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          plan_id: "basic",
          plan_name: "Basic Plan",
          price: 99,
          success_url: `${window.location.origin}/merchant?subscription=success`,
          cancel_url: `${window.location.origin}/merchant?subscription=cancelled`
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          toast.error("Failed to create checkout session");
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || "Failed to create checkout");
      }
    } catch (error) {
      console.error("Subscribe error:", error);
      toast.error("Failed to start subscription process");
    } finally {
      setSubscribing(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    fetchSubscriptionStatus();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [prodRes, catRes, ordRes, subRes, agrRes, sigRes, penRes, anaRes, platAgrRes, merchSubRes, pricingRes, historyRes, paymentStatusRes] = await Promise.all([
        fetch(`${API}/merchant/products`, { headers, credentials: "include" }),
        fetch(`${API}/merchant/categories`, { headers, credentials: "include" }),
        fetch(`${API}/merchant/orders`, { headers, credentials: "include" }),
        fetch(`${API}/merchant/subscriptions`, { headers, credentials: "include" }),
        fetch(`${API}/merchant/agreements`, { headers, credentials: "include" }),
        fetch(`${API}/merchant/all-signatures`, { headers, credentials: "include" }),
        fetch(`${API}/merchant/penalties`, { headers, credentials: "include" }),
        fetch(`${API}/merchant/analytics`, { headers, credentials: "include" }),
        fetch(`${API}/merchant/platform-agreement`, { headers, credentials: "include" }),
        fetch(`${API}/merchant/subscription`, { headers, credentials: "include" }),
        fetch(`${API}/pricing/v2`, { credentials: "include" }),
        fetch(`${API}/merchant/subscription/history`, { headers, credentials: "include" }),
        fetch(`${API}/merchant/payment/status`, { headers, credentials: "include" })
      ]);

      if (prodRes.ok) setProducts(await prodRes.json());
      if (catRes.ok) setCategories(await catRes.json());
      if (ordRes.ok) setOrders(await ordRes.json());
      if (subRes.ok) setSubscriptions(await subRes.json());
      if (agrRes.ok) setAgreements(await agrRes.json());
      if (sigRes.ok) setSignatures(await sigRes.json());
      if (penRes.ok) setPenalties(await penRes.json());
      if (anaRes.ok) setAnalytics(await anaRes.json());
      if (platAgrRes.ok) {
        const platData = await platAgrRes.json();
        setPlatformAgreement(platData.agreement);
      }
      if (merchSubRes.ok) {
        const merchSubData = await merchSubRes.json();
        setMerchantSubscription(merchSubData.subscription);
        if (merchSubData.feature_access) {
          setFeatureAccess(merchSubData.feature_access);
        }
      }
      if (pricingRes.ok) {
        setV2Pricing(await pricingRes.json());
      }
      if (historyRes.ok) {
        const histData = await historyRes.json();
        setSubscriptionHistory(histData.history || []);
      }
      if (paymentStatusRes.ok) {
        const paymentData = await paymentStatusRes.json();
        setPaymentStatus(paymentData);
        // Show payment setup modal if merchant is approved but has no payment method
        if (!paymentData.has_payment_method && user?.merchant_status === "approved") {
          // Delay to avoid showing immediately on page load
          setTimeout(() => setShowPaymentSetupModal(true), 1500);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle complete payment - V2 Pricing with base plan + add-ons
  const handleCompletePayment = async () => {
    setProcessingPayment(true);
    try {
      const res = await fetch(`${API}/stripe/v2/create-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify({
          addons: selectedAddons,
          discount_code: promoResult?.valid ? promoCode.trim() : null,
          success_url: `${window.location.origin}/merchant?payment=success&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/merchant/go-live?payment=canceled`
        })
      });

      const data = await res.json();

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.success && data.is_free_trial) {
        // Free trial activated directly without Stripe checkout
        toast.success("Free trial activated! Welcome to VEYRONIX!");
        window.location.reload();
      } else {
        throw new Error(data.detail || "Failed to create checkout session");
      }
    } catch (error) {
      toast.error(error.message || "Failed to process payment. Please try again.");
      setProcessingPayment(false);
    }
  };

  // Calculate V2 total price
  const calculateV2Total = () => {
    if (!v2Pricing) return 99; // Default base price
    let total = v2Pricing.base_plan?.price || 99;
    selectedAddons.forEach(addonId => {
      if (v2Pricing.addons[addonId]) {
        total += v2Pricing.addons[addonId].price;
      }
    });
    return total;
  };
  
  // Calculate V2 total with promo discount applied
  const calculateV2TotalWithDiscount = () => {
    const baseTotal = calculateV2Total();
    if (!promoResult || !promoResult.valid) return baseTotal;
    
    // Apply discount based on promo type
    if (promoResult.type === "free_trial" || promoResult.type === "free_onboarding_trial") {
      return 0; // First month free
    } else if (promoResult.type === "percentage" && promoResult.discount_percent > 0) {
      return baseTotal * (1 - promoResult.discount_percent / 100);
    } else if (promoResult.type === "free_onboarding") {
      // Only waives setup fee, not monthly
      return baseTotal;
    }
    return baseTotal;
  };
  
  // Validate promo code
  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoResult(null);
      return;
    }
    
    setPromoValidating(true);
    try {
      const res = await fetch(`${API}/validate-discount`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: promoCode.trim() })
      });
      const data = await res.json();
      setPromoResult(data);
      
      if (data.valid) {
        toast.success(data.message || "Promo code applied!");
      } else {
        toast.error(data.message || "Invalid promo code");
      }
    } catch (error) {
      toast.error("Failed to validate promo code");
      setPromoResult(null);
    } finally {
      setPromoValidating(false);
    }
  };

  // Fetch customer billing settings
  const fetchCustomerBillingSettings = async () => {
    try {
      const res = await fetch(`${API}/merchant/customer-billing`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        if (data.billing_cycles) {
          setCustomerBillingSettings({
            weekly: { enabled: data.billing_cycles.weekly?.enabled || false, price: data.billing_cycles.weekly?.price?.toString() || "" },
            monthly: { enabled: data.billing_cycles.monthly?.enabled || true, price: data.billing_cycles.monthly?.price?.toString() || "" },
            yearly: { enabled: data.billing_cycles.yearly?.enabled || false, price: data.billing_cycles.yearly?.price?.toString() || "" }
          });
        }
      }
    } catch (error) {
      console.error("Error fetching billing settings:", error);
    }
  };

  // Save customer billing settings
  const saveCustomerBillingSettings = async () => {
    setSavingBillingSettings(true);
    try {
      const res = await fetch(`${API}/merchant/customer-billing`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          billing_cycles: {
            weekly: { enabled: customerBillingSettings.weekly.enabled, price: parseFloat(customerBillingSettings.weekly.price) || 0 },
            monthly: { enabled: customerBillingSettings.monthly.enabled, price: parseFloat(customerBillingSettings.monthly.price) || 0 },
            yearly: { enabled: customerBillingSettings.yearly.enabled, price: parseFloat(customerBillingSettings.yearly.price) || 0 }
          },
          default_cycle: "monthly"
        })
      });
      
      if (res.ok) {
        toast.success("Customer billing settings saved!");
        setShowBillingSettingsModal(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save billing settings");
    } finally {
      setSavingBillingSettings(false);
    }
  };

  // Toggle addon selection
  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const copyAgreementLink = (agreementId) => {
    const link = `${window.location.origin}/agreement/${agreementId}`;
    navigator.clipboard.writeText(link);
    toast.success("Agreement link copied to clipboard!");
  };

  const viewSignatures = async (agreement) => {
    setSelectedAgreement(agreement);
    setShowSignaturesModal(true);
  };

  const openSendAgreementModal = (agreement) => {
    setSelectedAgreement(agreement);
    setSendAgreementForm({ email: "", name: "" });
    setShowSendAgreementModal(true);
  };

  const handleSendAgreementInvitation = async (e) => {
    e.preventDefault();
    if (!selectedAgreement) return;
    
    try {
      const response = await fetch(`${API}/merchant/agreements/${selectedAgreement.agreement_id}/send-invitation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify(sendAgreementForm)
      });
      
      if (response.ok) {
        toast.success(`Agreement invitation sent to ${sendAgreementForm.email}`);
        setShowSendAgreementModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Failed to send invitation");
    }
  };

  const openPenaltyModal = (subscription) => {
    setSelectedSubscription(subscription);
    setPenaltyForm({ 
      amount: "", 
      reason: "", 
      penalty_type: "other",
      proof_document_url: "",
      proof_document_name: "",
      additional_notes: "",
      charge_immediately: false
    });
    setShowPenaltyModal(true);
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    
    setUploadingProof(true);
    
    // Convert to base64 for storage (in production, use S3/cloud storage)
    const reader = new FileReader();
    reader.onloadend = () => {
      setPenaltyForm(prev => ({
        ...prev,
        proof_document_url: reader.result,
        proof_document_name: file.name
      }));
      setUploadingProof(false);
      toast.success("Proof document uploaded");
    };
    reader.onerror = () => {
      setUploadingProof(false);
      toast.error("Failed to upload document");
    };
    reader.readAsDataURL(file);
  };

  const handleSignPlatformAgreement = async () => {
    if (!merchantSignature) {
      toast.error("Please provide your signature");
      return;
    }
    
    try {
      const response = await fetch(`${API}/merchant/platform-agreement/sign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify({ signature_data: merchantSignature })
      });
      
      if (response.ok) {
        toast.success("Agreement signed! Awaiting admin counter-signature.");
        setShowPlatformAgreementModal(false);
        setMerchantSignature("");
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to sign agreement");
      }
    } catch (error) {
      toast.error("Failed to sign agreement");
    }
  };

  const handleApplyPenalty = async (e) => {
    e.preventDefault();
    if (!selectedSubscription) return;
    
    try {
      const response = await fetch(`${API}/merchant/subscriptions/${selectedSubscription.subscription_id}/penalty`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify(penaltyForm)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowPenaltyModal(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to apply penalty");
      }
    } catch (error) {
      toast.error("Failed to apply penalty");
    }
  };

  const handleUpdatePenaltyStatus = async (penaltyId, newStatus) => {
    try {
      const response = await fetch(`${API}/merchant/penalties/${penaltyId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Penalty marked as ${newStatus}`);
        fetchAllData();
      } else {
        toast.error("Failed to update penalty status");
      }
    } catch (error) {
      toast.error("Failed to update penalty status");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Product CRUD
  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/merchant/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({
          name: productForm.name,
          description: productForm.description,
          price: parseFloat(productForm.price),
          category_id: productForm.category_id,
          image_url: productForm.images?.[0] || null,
          is_subscription: productForm.is_subscription,
          subscription_interval: productForm.subscription_interval,
          offers_delivery: productForm.offers_delivery || false
        })
      });

      if (response.ok) {
        toast.success("Product created successfully");
        setShowProductModal(false);
        resetProductForm();
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create product");
      }
    } catch (error) {
      toast.error("Failed to create product");
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/merchant/products/${editingProduct.product_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({
          name: productForm.name,
          description: productForm.description,
          price: parseFloat(productForm.price),
          category_id: productForm.category_id,
          image_url: productForm.images?.[0] || null,
          is_subscription: productForm.is_subscription,
          subscription_interval: productForm.subscription_interval,
          offers_delivery: productForm.offers_delivery || false
        })
      });

      if (response.ok) {
        toast.success("Product updated successfully");
        setShowProductModal(false);
        setEditingProduct(null);
        resetProductForm();
        fetchAllData();
      } else {
        toast.error("Failed to update product");
      }
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      const response = await fetch(`${API}/merchant/products/${productId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Product deleted");
        fetchAllData();
      } else {
        toast.error("Failed to delete product");
      }
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  // Toggle product visibility (online/offline)
  const handleToggleProductVisibility = async (product) => {
    try {
      const response = await fetch(`${API}/merchant/products/${product.product_id}/toggle`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Update local state immediately
        setProducts(prev => prev.map(p => 
          p.product_id === product.product_id 
            ? { ...p, is_active: data.is_active }
            : p
        ));
      } else {
        toast.error("Failed to toggle product visibility");
      }
    } catch (error) {
      toast.error("Failed to toggle product visibility");
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: "",
      description: "",
      price: "",
      category_id: "",
      images: [],
      stock: 0,
      is_subscription: false,
      subscription_interval: "monthly",
      subscription_price: "",
      direct_debit_enabled: false,
      penalty_percentage: ""
    });
  };

  // Handle product image upload
  const handleProductImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, PNG, or WebP images allowed");
      return;
    }

    // Validate file size (1MB max)
    if (file.size > 1 * 1024 * 1024) {
      toast.error("Image must be under 1MB");
      return;
    }

    setUploadingProductImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/merchant/upload/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setProductForm(prev => ({
          ...prev,
          images: [data.image_url]
        }));
        toast.success("Image uploaded!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingProductImage(false);
      if (productImageRef.current) productImageRef.current.value = '';
    }
  };

  // Remove product image
  const removeProductImage = () => {
    setProductForm(prev => ({ ...prev, images: [] }));
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category_id: product.category_id,
      images: product.images || [],
      stock: product.stock || 0,
      is_subscription: product.is_subscription,
      subscription_interval: product.subscription_interval || "monthly",
      subscription_price: product.subscription_price?.toString() || "",
      direct_debit_enabled: product.direct_debit_enabled,
      penalty_percentage: product.penalty_percentage?.toString() || ""
    });
    setShowProductModal(true);
  };

  // Category CRUD
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/merchant/categories`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(categoryForm)
      });

      if (response.ok) {
        toast.success("Category created");
        setShowCategoryModal(false);
        setCategoryForm({ name: "", description: "" });
        fetchAllData();
      } else {
        toast.error("Failed to create category");
      }
    } catch (error) {
      toast.error("Failed to create category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Delete this category?")) return;
    
    try {
      const response = await fetch(`${API}/merchant/categories/${categoryId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Category deleted");
        fetchAllData();
      } else {
        toast.error("Failed to delete category");
      }
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  // Agreement CRUD
  const handleCreateAgreement = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/merchant/agreements`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(agreementForm)
      });

      if (response.ok) {
        toast.success("Agreement created");
        setShowAgreementModal(false);
        setAgreementForm({ product_id: "", terms: "", penalty_terms: "" });
        fetchAllData();
      } else {
        toast.error("Failed to create agreement");
      }
    } catch (error) {
      toast.error("Failed to create agreement");
    }
  };

  // Order status update
  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      const response = await fetch(`${API}/merchant/orders/${orderId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success("Order status updated");
        fetchAllData();
      } else {
        toast.error("Failed to update order");
      }
    } catch (error) {
      toast.error("Failed to update order");
    }
  };

  // Handle order refund
  const handleRefundOrder = async () => {
    if (!selectedOrderForRefund) return;
    
    setProcessingRefund(true);
    try {
      const response = await fetch(`${API}/merchant/orders/${selectedOrderForRefund.order_id}/refund`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        credentials: "include",
        body: JSON.stringify({
          amount: refundAmount ? parseFloat(refundAmount) : null,
          reason: refundReason
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Refund of £${data.amount?.toFixed(2)} processed successfully`);
        setShowRefundModal(false);
        setSelectedOrderForRefund(null);
        setRefundAmount("");
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to process refund");
      }
    } catch (error) {
      console.error("Refund error:", error);
      toast.error("Failed to process refund");
    } finally {
      setProcessingRefund(false);
    }
  };

  // Send receipt email
  const handleSendReceipt = async (orderId) => {
    try {
      const response = await fetch(`${API}/merchant/orders/${orderId}/send-receipt`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Receipt email sent!");
      } else {
        toast.error("Failed to send receipt");
      }
    } catch (error) {
      toast.error("Failed to send receipt");
    }
  };

  // Check if merchant needs onboarding OR has a pending platform agreement to sign
  // Check if merchant account is fully approved (agreement completed)
  // With 5% commission model: merchants get full dashboard access after approval
  // Only CRM and POS are gated (admin-enabled monthly subscriptions)
  const isFullyApproved = user?.is_approved || user?.merchant_status === "approved";

  // Dashboard only locked during onboarding (before approval)
  const isDashboardLocked = user?.role === "merchant" && !isFullyApproved;

  const needsOnboarding = user?.role === "merchant" && (
    !isFullyApproved &&
    (user?.merchant_status && user?.merchant_status !== "approved")
  );

  // V2 Feature gating - determines which add-ons are unlocked
  const hasCRM = featureAccess?.crm === true;
  const hasPOS = featureAccess?.pos === true;
  const hasServices = featureAccess?.services === true;

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState(null);
  const [showFullUpgradeModal, setShowFullUpgradeModal] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const handleUpgradeClick = (feature) => {
    setUpgradeFeature(feature);
    setShowUpgradeModal(true);
  };

  // Initialize selected addons from current subscription
  useEffect(() => {
    if (merchantSubscription) {
      const currentAddons = [];
      if (merchantSubscription.crm) currentAddons.push("crm");
      if (merchantSubscription.pos) currentAddons.push("pos");
      if (merchantSubscription.services) currentAddons.push("services");
      setSelectedAddons(currentAddons);
    }
  }, [merchantSubscription]);

  const calculateNewPrice = () => {
    let price = 99; // Base plan
    if (selectedAddons.includes("crm")) price += 79;
    if (selectedAddons.includes("pos")) price += 149;
    if (selectedAddons.includes("services")) price += 99;
    return price;
  };

  const handleSaveSubscriptionChanges = async () => {
    setUpgrading(true);
    try {
      const response = await fetch(`${API}/merchant/subscription/upgrade`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ addons: selectedAddons })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Subscription updated!");
        setShowFullUpgradeModal(false);
        // Refresh subscription data
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update subscription");
      }
    } catch (error) {
      toast.error("Failed to update subscription");
    } finally {
      setUpgrading(false);
    }
  };

  const menuItems = [
    // Show "Go Live" at top if merchant needs onboarding
    ...(needsOnboarding ? [{ id: "go-live", label: "Go Live", icon: Rocket, path: "/merchant/go-live", highlight: true }] : []),
    { id: "overview", label: "Overview", icon: LayoutDashboard, path: "/merchant" },
    { id: "store-settings", label: "Store Settings", icon: Building2, path: "/merchant/store-settings" },
    { id: "analytics", label: "Analytics", icon: BarChart3, path: "/merchant/analytics" },
    { id: "products", label: "Products", icon: Package, path: "/merchant/products" },
    { id: "categories", label: "Categories", icon: FolderOpen, path: "/merchant/categories" },
    { id: "orders", label: "Orders", icon: ShoppingCart, path: "/merchant/orders" },
    { id: "subscriptions", label: "Subscriptions", icon: RefreshCw, path: "/merchant/subscriptions" },
    { id: "invoices", label: "Invoices", icon: Receipt, path: "/merchant/invoices" },
    { id: "penalties", label: "Penalties", icon: AlertTriangle, path: "/merchant/penalties" },
    { id: "agreements", label: "Agreements", icon: FileText, path: "/merchant/agreements" },
    { id: "payouts", label: "Payouts", icon: Wallet, path: "/merchant/payouts" },
    { id: "billing", label: "Billing", icon: PoundSterling, path: "/merchant/billing" },
    { id: "feedback", label: "Feedback", icon: MessageSquare, path: "/merchant/pos/feedback" },
    // CRM & Marketing Section
    { id: "crm-divider", label: "CRM & Marketing", divider: true },
    { id: "crm", label: "CRM", icon: Users, path: "/merchant/crm", openInNewTab: true, featureGated: !hasCRM, addonId: "crm" },
    { id: "ai-social", label: "Veyronix AI Studio", icon: Smartphone, path: "/merchant/ai-social", highlight: true, openInNewTab: true },
    { id: "crm-services", label: "Services & Booking", icon: CalendarDays, path: "/merchant/services", openInNewTab: true },
    // POS Section
    { id: "pos-divider", label: "Point of Sale", divider: true },
    { id: "pos-terminal", label: "POS Terminal", icon: ShoppingCart, path: "/merchant/pos/terminal", highlight: hasPOS, featureGated: !hasPOS, addonId: "pos" },
    { id: "pos-products", label: "POS Products", icon: Package, path: "/merchant/pos/products", featureGated: !hasPOS, addonId: "pos" },
    { id: "pos-services", label: "Services & Booking", icon: CalendarDays, path: "/merchant/services" }
  ];

  const activeMenu = menuItems.find(item => 
    location.pathname === item.path || 
    (item.path === "/merchant" && location.pathname === "/merchant/") ||
    (item.id === "go-live" && location.pathname === "/merchant/go-live")
  )?.id || "overview";

  // Stats
  const totalRevenue = orders.filter(o => o.status === "paid").reduce((sum, o) => sum + (o.total_amount || 0), 0);
  const activeSubscriptions = subscriptions.filter(s => s.status === "active").length;

  const isRejected = user?.merchant_status === "rejected";

  // If rejected, show rejection page
  if (isRejected) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl border border-red-200 dark:border-red-800 p-8 text-center shadow-lg">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-white mb-2">Application Rejected</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {user?.rejection_reason || "Your application was not approved. Please contact support."}
          </p>
          <Button onClick={handleLogout} variant="outline" className="rounded-full">
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 shadow-sm transform transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex flex-col h-full">
          {/* Brand Header */}
          <div className="p-4 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 dark:from-indigo-500 dark:via-violet-500 dark:to-fuchsia-400">
            <Link to="/" className="flex items-center gap-3">
              <img src={COMPANY_LOGO} alt="V" className="h-10 w-auto brightness-0 invert" />
              <div className="h-8 w-px bg-white/40" />
              <span className="text-white font-brand text-lg font-bold">VEYRONIX</span>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map(item => (
              item.divider ? (
                <div key={item.id} className="pt-4 pb-2 px-4">
                  <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{item.label}</span>
                </div>
              ) : item.locked ? (
                // Locked menu items - show but disable
                <div
                  key={item.id}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60"
                  title="Complete onboarding to unlock"
                  data-testid={`menu-${item.id}-locked`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                  <Lock className="h-3 w-3 ml-auto" />
                </div>
              ) : item.featureGated ? (
                // Feature-gated items (CRM/POS) - show locked badge, contact admin
                <div
                  key={item.id}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-400 dark:text-gray-500 cursor-not-allowed"
                  title="Contact admin to enable this feature"
                  data-testid={`menu-${item.id}-locked`}
                >
                  <item.icon className="h-5 w-5 opacity-50" />
                  <span className="flex-1">{item.label}</span>
                  <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 rounded-full">
                    <Lock className="h-3 w-3 inline mr-1" />
                    Locked
                  </span>
                </div>
              ) : item.openInNewTab ? (
                <a
                  key={item.id}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    item.highlight
                      ? "bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 text-white shadow-lg"
                      : activeMenu === item.id
                      ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                  data-testid={`menu-${item.id}`}
                >
                  <item.icon className={`h-5 w-5 ${item.highlight ? "animate-pulse" : ""}`} />
                  {item.label}
                  <ExternalLink className="h-3 w-3 ml-auto opacity-70" />
                </a>
              ) : (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    item.highlight
                      ? "bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 text-white shadow-lg"
                      : activeMenu === item.id
                      ? "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                  data-testid={`menu-${item.id}`}
                >
                  <item.icon className={`h-5 w-5 ${item.highlight ? "animate-pulse" : ""}`} />
                  {item.label}
                  {item.highlight && item.id === "go-live" && (
                    <span className="ml-auto text-xs bg-white/20 px-2 py-0.5 rounded-full">Action Required</span>
                  )}
                </Link>
              )
            ))}
          </nav>

          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                <span className="font-heading font-bold text-indigo-600 dark:text-indigo-400">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.business_name || "Merchant"}</p>
              </div>
              <ThemeToggleCompact />
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full rounded-full text-sm dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 -ml-2 dark:text-gray-300"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="font-heading text-xl font-bold text-slate-900 dark:text-white" data-testid="merchant-dashboard">
                {menuItems.find(m => m.id === activeMenu)?.label || "Dashboard"}
              </h1>
            </div>
            <Link to="/store">
              <Button variant="outline" className="rounded-full text-sm">
                View Store
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : isDashboardLocked && activeMenu !== "go-live" ? (
            /* Locked Dashboard - redirect to Go Live */
            <div className="max-w-lg mx-auto text-center py-16">
              <div className="w-20 h-20 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-6">
                <Lock className="h-10 w-10 text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-gray-900 dark:text-white mb-3">
                Dashboard Locked
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
                Complete the onboarding process to unlock your merchant dashboard and start selling on VEYRONIX.
              </p>
              <Link to="/merchant/go-live">
                <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-full px-8">
                  <Rocket className="h-4 w-4 mr-2" />
                  Complete Onboarding
                </Button>
              </Link>
            </div>
          ) : activeMenu === "go-live" ? (
            <GoLiveSection user={user} platformAgreement={platformAgreement} />
          ) : activeMenu === "overview" ? (
            <MerchantOverview user={user} needsOnboarding={needsOnboarding} products={products} orders={orders} totalRevenue={totalRevenue} activeSubscriptions={activeSubscriptions} onAddProduct={() => { resetProductForm(); setEditingProduct(null); setShowProductModal(true); }} onAddCategory={() => setShowCategoryModal(true)} onCreateAgreement={() => setShowAgreementModal(true)} />
          ) : activeMenu === "analytics" ? (
            /* Analytics */
            <div className="space-y-6">
              <h2 className="font-heading font-semibold text-lg text-slate-900">Business Analytics</h2>
              
              {!analytics ? (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-gray-500">Loading analytics...</p>
                </div>
              ) : (
                <>
                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <PoundSterling className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-xl font-heading font-bold text-slate-900">£{analytics.total_revenue?.toFixed(2) || "0.00"}</p>
                          <p className="text-xs text-gray-500">Total Revenue</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <ShoppingCart className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-xl font-heading font-bold text-slate-900">{analytics.total_orders || 0}</p>
                          <p className="text-xs text-gray-500">Total Orders</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                          <RefreshCw className="h-5 w-5 text-cyan-600" />
                        </div>
                        <div>
                          <p className="text-xl font-heading font-bold text-slate-900">{analytics.active_subscriptions || 0}</p>
                          <p className="text-xs text-gray-500">Active Subs</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-xl font-heading font-bold text-slate-900">£{analytics.total_pending_penalties?.toFixed(2) || "0.00"}</p>
                          <p className="text-xs text-gray-500">Pending Penalties</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Revenue Chart */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="font-heading font-semibold text-slate-900 mb-4">Revenue (Last 30 Days)</h3>
                    <div className="h-48 flex items-end gap-1">
                      {analytics.revenue_chart?.slice(-30).map((day, idx) => {
                        const maxRevenue = Math.max(...analytics.revenue_chart.map(d => d.revenue), 1);
                        const height = (day.revenue / maxRevenue) * 100;
                        return (
                          <div 
                            key={day.date} 
                            className="flex-1 bg-indigo-500 rounded-t hover:bg-indigo-600 transition-colors cursor-pointer group relative"
                            style={{ height: `${Math.max(height, 2)}%` }}
                            title={`${day.date}: £${day.revenue.toFixed(2)}`}
                          >
                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                              £{day.revenue.toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-gray-400">
                      <span>30 days ago</span>
                      <span>Today</span>
                    </div>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Order Status Distribution */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="font-heading font-semibold text-slate-900 mb-4">Order Status</h3>
                      <div className="space-y-3">
                        {Object.entries(analytics.order_statuses || {}).map(([status, count]) => {
                          const total = Object.values(analytics.order_statuses).reduce((a, b) => a + b, 0);
                          const percentage = total > 0 ? (count / total) * 100 : 0;
                          return (
                            <div key={status}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="capitalize text-gray-600">{status}</span>
                                <span className="font-semibold text-slate-900">{count}</span>
                              </div>
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${
                                    status === "paid" ? "bg-emerald-500" :
                                    status === "pending" ? "bg-amber-500" :
                                    status === "shipped" ? "bg-blue-500" :
                                    status === "delivered" ? "bg-green-500" :
                                    "bg-slate-400"
                                  }`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Top Products */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="font-heading font-semibold text-slate-900 mb-4">Top Products</h3>
                      {analytics.top_products?.length === 0 ? (
                        <p className="text-gray-500 text-sm">No product data yet</p>
                      ) : (
                        <div className="space-y-3">
                          {analytics.top_products?.map((product, idx) => (
                            <div key={product.product_id} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-semibold">
                                  {idx + 1}
                                </span>
                                <span className="text-sm text-gray-700">{product.name}</span>
                              </div>
                              <span className="text-sm font-semibold text-slate-900">{product.orders} orders</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                      <p className="text-2xl font-heading font-bold text-slate-900">{analytics.products_count || 0}</p>
                      <p className="text-xs text-gray-500">Products Listed</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                      <p className="text-2xl font-heading font-bold text-slate-900">{analytics.signatures_count || 0}</p>
                      <p className="text-xs text-gray-500">Agreements Signed</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                      <p className="text-2xl font-heading font-bold text-slate-900">{analytics.pending_penalties || 0}</p>
                      <p className="text-xs text-gray-500">Pending Penalties</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : activeMenu === "agreements" ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500">{agreements.length} agreements</p>
                  <p className="text-xs text-gray-400">{signatures.length} total signatures received</p>
                </div>
                <Button onClick={() => setShowAgreementModal(true)} className="bg-indigo-600 hover:bg-indigo-700 rounded-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Agreement
                </Button>
              </div>
              
              {agreements.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-slate-900 mb-2">No agreements yet</h3>
                  <p className="text-gray-500 mb-4">Create agreements for subscription products that customers must sign.</p>
                  <Button onClick={() => setShowAgreementModal(true)} className="bg-indigo-600 hover:bg-indigo-700 rounded-full">
                    Create First Agreement
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {agreements.map((agr, idx) => {
                    const agreementSignatures = signatures.filter(s => s.agreement_id === agr.agreement_id);
                    const productName = products.find(p => p.product_id === agr.product_id)?.name || "Unknown Product";
                    
                    return (
                      <div key={agr.agreement_id} className="bg-white rounded-xl border border-gray-200 p-6" data-testid={`agreement-row-${idx}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="h-4 w-4 text-indigo-500" />
                              <span className="font-semibold text-slate-900">{productName}</span>
                            </div>
                            <p className="font-mono text-xs text-gray-400 mb-2">{agr.agreement_id}</p>
                            <p className="text-sm text-gray-600 line-clamp-2">{agr.terms}</p>
                            {agr.penalty_terms && (
                              <p className="text-sm text-amber-600 mt-2">⚠️ Penalty: {agr.penalty_terms}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              agreementSignatures.length > 0 ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"
                            }`}>
                              {agreementSignatures.length} signature{agreementSignatures.length !== 1 ? "s" : ""}
                            </span>
                            <div className="flex gap-1">
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                onClick={() => openSendAgreementModal(agr)}
                                className="h-8 w-8"
                                title="Send to customer"
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                onClick={() => copyAgreementLink(agr.agreement_id)}
                                className="h-8 w-8"
                                title="Copy signing link"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <a 
                                href={`/agreement/${agr.agreement_id}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                              >
                                <Button size="icon" variant="ghost" className="h-8 w-8" title="Preview agreement">
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              </a>
                              {agreementSignatures.length > 0 && (
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  onClick={() => viewSignatures(agr)}
                                  className="h-8 w-8"
                                  title="View signatures"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </main>

      {/* Signatures Modal */}
      <Dialog open={showSignaturesModal} onOpenChange={setShowSignaturesModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Signed Agreements</DialogTitle>
            <DialogDescription>
              {selectedAgreement && `Customers who signed this agreement`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {selectedAgreement && signatures
              .filter(s => s.agreement_id === selectedAgreement.agreement_id)
              .map((sig, idx) => (
                <div key={sig.signature_id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-slate-900">{sig.customer_name}</span>
                    <span className="text-xs text-gray-400">
                      {new Date(sig.signed_at).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{sig.customer_email}</p>
                  <p className="text-xs text-gray-400 font-mono mt-1">{sig.signature_id}</p>
                  {sig.signature_type === "drawn" && sig.signature_data?.startsWith("data:image") && (
                    <div className="mt-2 p-2 bg-white rounded border">
                      <img src={sig.signature_data} alt="Signature" className="h-12 object-contain" />
                    </div>
                  )}
                  {sig.signature_type === "typed" && (
                    <div className="mt-2 p-2 bg-white rounded border">
                      <p className="font-signature text-lg italic text-gray-700">{sig.signature_data}</p>
                    </div>
                  )}
                </div>
              ))
            }
            {selectedAgreement && signatures.filter(s => s.agreement_id === selectedAgreement.agreement_id).length === 0 && (
              <p className="text-center text-gray-500 py-4">No signatures yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
            <DialogDescription>Fill in the product details below.</DialogDescription>
          </DialogHeader>
          <form onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={productForm.name}
                onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                placeholder="Product name"
                required
                data-testid="product-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                placeholder="Product description"
                required
                data-testid="product-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={productForm.price}
                  onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                  placeholder="0.00"
                  required
                  data-testid="product-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Stock</Label>
                <Input
                  type="number"
                  value={productForm.stock}
                  onChange={(e) => setProductForm({...productForm, stock: parseInt(e.target.value) || 0})}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={productForm.category_id} onValueChange={(val) => setProductForm({...productForm, category_id: val})}>
                <SelectTrigger data-testid="product-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Product Image Upload */}
            <div className="space-y-2">
              <Label>Product Image (optional)</Label>
              <div className="flex items-center gap-4">
                {productForm.images?.[0] ? (
                  <div className="relative">
                    <img 
                      src={productForm.images[0].startsWith('data:') ? productForm.images[0] :
                           (productForm.images[0].startsWith('http') ? productForm.images[0] : `${API}${productForm.images[0]}`)}
                      alt="Product" 
                      className="w-20 h-20 object-cover rounded-lg border"
                      data-testid="product-image-preview"
                    />
                    <button
                      type="button"
                      onClick={removeProductImage}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                    <Upload className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    ref={productImageRef}
                    onChange={handleProductImageUpload}
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    className="hidden"
                    data-testid="product-image-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => productImageRef.current?.click()}
                    disabled={uploadingProductImage}
                    data-testid="upload-product-image-btn"
                  >
                    {uploadingProductImage ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Image
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">Max 1MB (JPG, PNG)</p>
                </div>
              </div>
            </div>

            {/* Subscription settings */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <Label>Enable Subscription</Label>
                <Switch
                  checked={productForm.is_subscription}
                  onCheckedChange={(val) => setProductForm({...productForm, is_subscription: val})}
                  data-testid="subscription-toggle"
                />
              </div>
              
              {productForm.is_subscription && (
                <div className="space-y-4 animate-fade-in">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Interval</Label>
                      <Select value={productForm.subscription_interval} onValueChange={(val) => setProductForm({...productForm, subscription_interval: val})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="yearly">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Subscription Price (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={productForm.subscription_price}
                        onChange={(e) => setProductForm({...productForm, subscription_price: e.target.value})}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Enable Direct Debit</Label>
                    <Switch
                      checked={productForm.direct_debit_enabled}
                      onCheckedChange={(val) => setProductForm({...productForm, direct_debit_enabled: val})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Penalty Percentage (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={productForm.penalty_percentage}
                      onChange={(e) => setProductForm({...productForm, penalty_percentage: e.target.value})}
                      placeholder="e.g., 5"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowProductModal(false)} className="flex-1 rounded-full">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="save-product-btn">
                {editingProduct ? "Update" : "Create"} Product
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Category</DialogTitle>
            <DialogDescription>Select a category from the library or create a custom one.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCategory} className="space-y-4">
            <div className="space-y-2">
              <Label>Select from Library</Label>
              <Select
                value={categoryForm.name}
                onValueChange={(value) => {
                  const selected = CATEGORY_LIBRARY.find(c => c.name === value);
                  if (selected) {
                    setCategoryForm({ name: selected.name, description: selected.description });
                  }
                }}
              >
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="Choose a category..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Retail & Products</div>
                  {CATEGORY_LIBRARY.slice(0, 11).map((cat) => (
                    <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Services</div>
                  {CATEGORY_LIBRARY.slice(11, 21).map((cat) => (
                    <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Food & Hospitality</div>
                  {CATEGORY_LIBRARY.slice(21, 27).map((cat) => (
                    <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Salon & Beauty</div>
                  {CATEGORY_LIBRARY.slice(27, 33).map((cat) => (
                    <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Automotive</div>
                  {CATEGORY_LIBRARY.slice(33, 36).map((cat) => (
                    <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Real Estate</div>
                  {CATEGORY_LIBRARY.slice(36, 39).map((cat) => (
                    <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Other</div>
                  {CATEGORY_LIBRARY.slice(39).map((cat) => (
                    <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Or create custom</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                placeholder="Enter custom category name"
                required
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                placeholder="Category description"
                rows={2}
              />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => { setShowCategoryModal(false); setCategoryForm({ name: "", description: "" }); }} className="flex-1 rounded-full">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="save-category-btn">
                Create Category
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Agreement Modal */}
      <Dialog open={showAgreementModal} onOpenChange={setShowAgreementModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Agreement</DialogTitle>
            <DialogDescription>Create a subscription agreement for a product.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAgreement} className="space-y-4">
            <div className="space-y-2">
              <Label>Product *</Label>
              <Select value={agreementForm.product_id} onValueChange={(val) => setAgreementForm({...agreementForm, product_id: val})}>
                <SelectTrigger data-testid="agreement-product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.filter(p => p.is_subscription).map(prod => (
                    <SelectItem key={prod.product_id} value={prod.product_id}>{prod.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Terms & Conditions *</Label>
              <Textarea
                value={agreementForm.terms}
                onChange={(e) => setAgreementForm({...agreementForm, terms: e.target.value})}
                placeholder="Enter the agreement terms..."
                rows={4}
                required
                data-testid="agreement-terms-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Penalty Terms</Label>
              <Textarea
                value={agreementForm.penalty_terms}
                onChange={(e) => setAgreementForm({...agreementForm, penalty_terms: e.target.value})}
                placeholder="Enter penalty terms..."
                rows={2}
              />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowAgreementModal(false)} className="flex-1 rounded-full">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="save-agreement-btn">
                Create Agreement
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send Agreement Modal */}
      <Dialog open={showSendAgreementModal} onOpenChange={setShowSendAgreementModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Send Agreement Invitation</DialogTitle>
            <DialogDescription>
              Send an email invitation to sign this agreement.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendAgreementInvitation} className="space-y-4">
            <div className="space-y-2">
              <Label>Customer Email *</Label>
              <Input
                type="email"
                value={sendAgreementForm.email}
                onChange={(e) => setSendAgreementForm({...sendAgreementForm, email: e.target.value})}
                placeholder="customer@email.com"
                required
                data-testid="send-agreement-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Customer Name (optional)</Label>
              <Input
                value={sendAgreementForm.name}
                onChange={(e) => setSendAgreementForm({...sendAgreementForm, name: e.target.value})}
                placeholder="Customer name"
              />
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
              <p>The customer will receive an email with a link to sign the agreement.</p>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowSendAgreementModal(false)} className="flex-1 rounded-full">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="send-agreement-btn">
                <Send className="h-4 w-4 mr-2" />
                Send Invitation
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Apply Penalty Modal */}
      <Dialog open={showPenaltyModal} onOpenChange={setShowPenaltyModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Apply Penalty Charge</DialogTitle>
            <DialogDescription>
              Charge a penalty to this subscription customer with supporting documentation.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApplyPenalty} className="space-y-4">
            {selectedSubscription && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-mono text-xs text-gray-500">{selectedSubscription.subscription_id}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Penalty Type *</Label>
              <select
                value={penaltyForm.penalty_type}
                onChange={(e) => setPenaltyForm({...penaltyForm, penalty_type: e.target.value})}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              >
                <option value="late_payment">Late Payment</option>
                <option value="breach_of_contract">Breach of Contract</option>
                <option value="damage">Damage/Loss</option>
                <option value="cancellation">Early Cancellation</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>Penalty Amount (£) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={penaltyForm.amount}
                onChange={(e) => setPenaltyForm({...penaltyForm, amount: e.target.value})}
                placeholder="0.00"
                required
                data-testid="penalty-amount-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Reason / Description *</Label>
              <Textarea
                value={penaltyForm.reason}
                onChange={(e) => setPenaltyForm({...penaltyForm, reason: e.target.value})}
                placeholder="e.g., Late payment as per agreement clause 3.2"
                rows={2}
                required
                data-testid="penalty-reason-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Proof Document (PDF) *</Label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                {penaltyForm.proof_document_url ? (
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-indigo-600" />
                      <span className="text-sm text-gray-700">{penaltyForm.proof_document_name}</span>
                    </div>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setPenaltyForm({...penaltyForm, proof_document_url: "", proof_document_name: ""})}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleProofUpload}
                      className="hidden"
                    />
                    <div className="py-4">
                      {uploadingProof ? (
                        <RefreshCw className="h-8 w-8 text-gray-400 mx-auto animate-spin" />
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">Click to upload penalty letter/proof (PDF)</p>
                          <p className="text-xs text-gray-400 mt-1">Max 10MB</p>
                        </>
                      )}
                    </div>
                  </label>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Additional Notes (optional)</Label>
              <Textarea
                value={penaltyForm.additional_notes}
                onChange={(e) => setPenaltyForm({...penaltyForm, additional_notes: e.target.value})}
                placeholder="Any additional information about this penalty..."
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <input
                type="checkbox"
                id="charge_immediately"
                checked={penaltyForm.charge_immediately}
                onChange={(e) => setPenaltyForm({...penaltyForm, charge_immediately: e.target.checked})}
                className="rounded border-emerald-300"
              />
              <label htmlFor="charge_immediately" className="text-sm text-emerald-700 cursor-pointer">
                <strong>Charge immediately</strong> - Process this penalty charge right away
              </label>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              The customer will be notified via email about this penalty charge.
            </div>
            
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowPenaltyModal(false)} className="flex-1 rounded-full">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full" data-testid="apply-penalty-btn">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Apply Penalty
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Platform Agreement Modal for Merchant Signing */}
      <Dialog open={showPlatformAgreementModal} onOpenChange={setShowPlatformAgreementModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Platform Agreement</DialogTitle>
            <DialogDescription>
              Review and sign the platform agreement with VEYRONIX
            </DialogDescription>
          </DialogHeader>
          
          {platformAgreement && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Agreement Details</h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{platformAgreement.agreement_content}</p>
                </div>
                
                <div className="border-t pt-3">
                  <h4 className="font-semibold text-slate-900 mb-2">Terms & Conditions</h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{platformAgreement.terms_and_conditions}</p>
                </div>
                
                <div className="border-t pt-3 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Platform Commission</p>
                    <p className="font-semibold text-slate-900">{platformAgreement.commission_rate}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Transaction Fee</p>
                    <p className="font-semibold text-slate-900">{platformAgreement.transaction_fee}%</p>
                  </div>
                </div>
                
                {platformAgreement.payment_terms && (
                  <div className="border-t pt-3">
                    <p className="text-xs text-gray-500">Payment Terms</p>
                    <p className="text-sm text-gray-600">{platformAgreement.payment_terms}</p>
                  </div>
                )}
                
                {platformAgreement.effective_date && (
                  <div className="border-t pt-3">
                    <p className="text-xs text-gray-500">Effective Date</p>
                    <p className="text-sm text-gray-600">{new Date(platformAgreement.effective_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Your Signature (Type your full name) *</Label>
                <Input
                  value={merchantSignature}
                  onChange={(e) => setMerchantSignature(e.target.value)}
                  placeholder="Type your full name as signature"
                />
                <p className="text-xs text-gray-500">
                  By signing, you agree to the terms and conditions outlined in this agreement.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowPlatformAgreementModal(false)} 
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSignPlatformAgreement}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 rounded-full"
                  disabled={!merchantSignature}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Sign Agreement
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upgrade Feature Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="max-w-md" data-testid="upgrade-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-500" />
              Upgrade to Unlock {upgradeFeature?.name}
            </DialogTitle>
            <DialogDescription>
              Add the {upgradeFeature?.id === "crm" ? "CRM" : upgradeFeature?.id === "pos" ? "POS" : "Services & Booking"} add-on to your subscription.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-slate-900 dark:text-white">{upgradeFeature?.name}</span>
                <span className="text-lg font-bold text-amber-600 dark:text-amber-400">£{upgradeFeature?.price}/mo</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {upgradeFeature?.id === "crm" && "Manage customer relationships, email marketing, and loyalty programs."}
                {upgradeFeature?.id === "pos" && "Full point-of-sale terminal, inventory management, and sales tracking."}
                {upgradeFeature?.id === "services" && "Online booking system, service management, and appointment scheduling."}
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 rounded-full"
              >
                Not Now
              </Button>
              <Button
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate("/merchant/subscription-manage");
                }}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full"
                data-testid="upgrade-now-btn"
              >
                <Zap className="h-4 w-4 mr-2" />
                Upgrade Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Subscription Upgrade Modal */}
      <Dialog open={showFullUpgradeModal} onOpenChange={setShowFullUpgradeModal}>
        <DialogContent className="max-w-lg" data-testid="full-upgrade-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Settings className="h-5 w-5 text-indigo-500" />
              Manage Your Subscription
            </DialogTitle>
            <DialogDescription>
              Add or remove add-ons to customize your plan. Changes take effect immediately.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Base Plan - Always included */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 border border-indigo-200 dark:border-indigo-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center">
                    <Zap className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900 dark:text-white">Basic Plan</span>
                    <p className="text-xs text-gray-500">Core platform access (Required)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-indigo-600">£99</span>
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">Required</span>
                </div>
              </div>
            </div>

            {/* CRM Add-on */}
            <div 
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedAddons.includes("crm") 
                  ? "bg-blue-50 dark:bg-blue-900/20 border-blue-400" 
                  : "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:border-blue-300"
              }`}
              onClick={() => toggleAddon("crm")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedAddons.includes("crm") ? "bg-blue-100" : "bg-gray-100"
                  }`}>
                    <Users className={`h-5 w-5 ${selectedAddons.includes("crm") ? "text-blue-600" : "text-gray-400"}`} />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900 dark:text-white">CRM</span>
                    <p className="text-xs text-gray-500">Customer management & marketing</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-bold ${selectedAddons.includes("crm") ? "text-blue-600" : "text-gray-400"}`}>+£79</span>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedAddons.includes("crm") ? "bg-blue-500 border-blue-500" : "border-gray-300"
                  }`}>
                    {selectedAddons.includes("crm") && <Check className="h-4 w-4 text-white" />}
                  </div>
                </div>
              </div>
            </div>

            {/* POS Add-on */}
            <div 
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedAddons.includes("pos") 
                  ? "bg-purple-50 dark:bg-purple-900/20 border-purple-400" 
                  : "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:border-purple-300"
              }`}
              onClick={() => toggleAddon("pos")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedAddons.includes("pos") ? "bg-purple-100" : "bg-gray-100"
                  }`}>
                    <ShoppingCart className={`h-5 w-5 ${selectedAddons.includes("pos") ? "text-purple-600" : "text-gray-400"}`} />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900 dark:text-white">POS Terminal</span>
                    <p className="text-xs text-gray-500">Point of sale & inventory</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-bold ${selectedAddons.includes("pos") ? "text-purple-600" : "text-gray-400"}`}>+£149</span>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedAddons.includes("pos") ? "bg-purple-500 border-purple-500" : "border-gray-300"
                  }`}>
                    {selectedAddons.includes("pos") && <Check className="h-4 w-4 text-white" />}
                  </div>
                </div>
              </div>
            </div>

            {/* Services Add-on */}
            <div 
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedAddons.includes("services") 
                  ? "bg-pink-50 dark:bg-pink-900/20 border-pink-400" 
                  : "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:border-pink-300"
              }`}
              onClick={() => toggleAddon("services")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedAddons.includes("services") ? "bg-pink-100" : "bg-gray-100"
                  }`}>
                    <CalendarDays className={`h-5 w-5 ${selectedAddons.includes("services") ? "text-pink-600" : "text-gray-400"}`} />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900 dark:text-white">Services & Booking</span>
                    <p className="text-xs text-gray-500">Online booking & appointments</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-bold ${selectedAddons.includes("services") ? "text-pink-600" : "text-gray-400"}`}>+£99</span>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedAddons.includes("services") ? "bg-pink-500 border-pink-500" : "border-gray-300"
                  }`}>
                    {selectedAddons.includes("services") && <Check className="h-4 w-4 text-white" />}
                  </div>
                </div>
              </div>
            </div>

            {/* Total */}
            <div className="mt-4 p-4 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl text-white">
              <div className="flex items-center justify-between">
                <span className="font-medium">New Monthly Total</span>
                <span className="text-2xl font-bold">£{calculateNewPrice()}/mo</span>
              </div>
              <p className="text-xs text-white/70 mt-1">
                Changes will be reflected in your next billing cycle
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowFullUpgradeModal(false)}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSubscriptionChanges}
              disabled={upgrading}
              className="flex-1 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white rounded-full"
              data-testid="save-subscription-btn"
            >
              {upgrading ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Check className="h-4 w-4 mr-2" /> Save Changes</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="subscription-history-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-500" />
              Subscription History
            </DialogTitle>
            <DialogDescription>
              Your subscription changes and modifications
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {subscriptionHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <History className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No subscription changes yet</p>
              </div>
            ) : (
              subscriptionHistory.map((entry, idx) => (
                <div 
                  key={entry.history_id || idx} 
                  className={`p-4 rounded-xl border-2 ${
                    entry.change_type === "upgrade" 
                      ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800"
                      : entry.change_type === "downgrade"
                      ? "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800"
                      : "bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        entry.change_type === "upgrade" 
                          ? "bg-emerald-100 text-emerald-600"
                          : entry.change_type === "downgrade"
                          ? "bg-amber-100 text-amber-600"
                          : "bg-gray-100 text-gray-600"
                      }`}>
                        {entry.change_type === "upgrade" ? (
                          <ArrowUpRight className="h-4 w-4" />
                        ) : entry.change_type === "downgrade" ? (
                          <ArrowDownRight className="h-4 w-4" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </div>
                      <div>
                        <span className={`text-sm font-medium capitalize ${
                          entry.change_type === "upgrade" 
                            ? "text-emerald-700 dark:text-emerald-400"
                            : entry.change_type === "downgrade"
                            ? "text-amber-700 dark:text-amber-400"
                            : "text-gray-700 dark:text-gray-300"
                        }`}>
                          {entry.change_type}
                        </span>
                        <p className="text-xs text-gray-500">
                          {new Date(entry.effective_date).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`font-bold ${
                        entry.price_change > 0 
                          ? "text-emerald-600"
                          : entry.price_change < 0
                          ? "text-amber-600"
                          : "text-gray-600"
                      }`}>
                        {entry.price_change > 0 ? "+" : ""}£{entry.price_change}/mo
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-xs">
                    <div className="flex justify-between text-gray-500">
                      <span>Previous:</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        £{entry.previous_plan?.price || 99} 
                        {entry.previous_plan?.addons?.length > 0 && ` (${entry.previous_plan.addons.join(", ").toUpperCase()})`}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-500 mt-1">
                      <span>New:</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        £{entry.new_plan?.price || 99}
                        {entry.new_plan?.addons?.length > 0 && ` (${entry.new_plan.addons.join(", ").toUpperCase()})`}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <Button
            variant="outline"
            onClick={() => setShowHistoryModal(false)}
            className="w-full rounded-full"
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>

      {/* Customer Billing Settings Modal */}
      <Dialog open={showBillingSettingsModal} onOpenChange={setShowBillingSettingsModal}>
        <DialogContent className="max-w-md" data-testid="customer-billing-settings-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <PoundSterling className="h-5 w-5 text-indigo-500" />
              Customer Billing Settings
            </DialogTitle>
            <DialogDescription>
              Set pricing for customers subscribing to your services
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Weekly */}
            <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
              <input
                type="checkbox"
                checked={customerBillingSettings.weekly.enabled}
                onChange={(e) => setCustomerBillingSettings(prev => ({
                  ...prev,
                  weekly: { ...prev.weekly, enabled: e.target.checked }
                }))}
                className="w-5 h-5 rounded"
                data-testid="weekly-enabled-checkbox"
              />
              <div className="flex-1">
                <Label className="font-medium">Weekly</Label>
                <p className="text-xs text-gray-500">Charge customers weekly</p>
              </div>
              <div className="w-24">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="£ Price"
                  value={customerBillingSettings.weekly.price}
                  onChange={(e) => setCustomerBillingSettings(prev => ({
                    ...prev,
                    weekly: { ...prev.weekly, price: e.target.value }
                  }))}
                  disabled={!customerBillingSettings.weekly.enabled}
                  className="text-sm"
                  data-testid="weekly-price-input"
                />
              </div>
            </div>

            {/* Monthly */}
            <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-indigo-50/50 dark:bg-indigo-900/10">
              <input
                type="checkbox"
                checked={customerBillingSettings.monthly.enabled}
                onChange={(e) => setCustomerBillingSettings(prev => ({
                  ...prev,
                  monthly: { ...prev.monthly, enabled: e.target.checked }
                }))}
                className="w-5 h-5 rounded"
                data-testid="monthly-enabled-checkbox"
              />
              <div className="flex-1">
                <Label className="font-medium">Monthly <span className="text-indigo-600 text-xs ml-1">Recommended</span></Label>
                <p className="text-xs text-gray-500">Standard billing cycle</p>
              </div>
              <div className="w-24">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="£ Price"
                  value={customerBillingSettings.monthly.price}
                  onChange={(e) => setCustomerBillingSettings(prev => ({
                    ...prev,
                    monthly: { ...prev.monthly, price: e.target.value }
                  }))}
                  disabled={!customerBillingSettings.monthly.enabled}
                  className="text-sm"
                  data-testid="monthly-price-input"
                />
              </div>
            </div>

            {/* Yearly */}
            <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
              <input
                type="checkbox"
                checked={customerBillingSettings.yearly.enabled}
                onChange={(e) => setCustomerBillingSettings(prev => ({
                  ...prev,
                  yearly: { ...prev.yearly, enabled: e.target.checked }
                }))}
                className="w-5 h-5 rounded"
                data-testid="yearly-enabled-checkbox"
              />
              <div className="flex-1">
                <Label className="font-medium">Yearly</Label>
                <p className="text-xs text-gray-500">Annual subscription discount</p>
              </div>
              <div className="w-24">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="£ Price"
                  value={customerBillingSettings.yearly.price}
                  onChange={(e) => setCustomerBillingSettings(prev => ({
                    ...prev,
                    yearly: { ...prev.yearly, price: e.target.value }
                  }))}
                  disabled={!customerBillingSettings.yearly.enabled}
                  className="text-sm"
                  data-testid="yearly-price-input"
                />
              </div>
            </div>

            <div className="text-xs text-gray-500 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <strong>Note:</strong> These prices will be shown to customers when they subscribe to your services. Stripe will handle billing at the selected interval.
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowBillingSettingsModal(false)}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={saveCustomerBillingSettings}
              disabled={savingBillingSettings}
              className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-billing-settings-btn"
            >
              {savingBillingSettings ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Settings"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Setup Modal */}
      <Dialog open={showPaymentSetupModal} onOpenChange={setShowPaymentSetupModal}>
        <DialogContent className="max-w-md" data-testid="payment-setup-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-indigo-600" />
              {paymentStatus?.has_payment_method ? "Update Payment Method" : "Setup Payment Method"}
            </DialogTitle>
            <DialogDescription>
              {paymentStatus?.has_payment_method 
                ? "Update your payment card for automatic billing."
                : "Add a payment method to enable automatic subscription billing. You won't be charged now."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {paymentStatus?.has_payment_method && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">Current payment method:</p>
                <p className="font-medium text-slate-900">
                  {paymentStatus.payment_method_brand?.toUpperCase()} •••• {paymentStatus.payment_method_last4}
                </p>
              </div>
            )}
            
            <div className="bg-indigo-50 rounded-lg p-4 text-sm text-indigo-800">
              <p className="font-medium">Secure payment processing</p>
              <p className="mt-1 text-indigo-600">
                Your payment information is handled securely by Stripe. We'll redirect you to add your card.
              </p>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Lock className="h-4 w-4" />
              <span>Your card will only be charged on your billing date</span>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowPaymentSetupModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                setSettingUpPayment(true);
                try {
                  const res = await fetch(`${API}/merchant/payment/setup-intent`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ origin_url: window.location.origin })
                  });
                  
                  if (!res.ok) throw new Error("Failed to create setup intent");
                  
                  const data = await res.json();
                  
                  // Redirect to Stripe for card setup
                  const stripeUrl = `https://checkout.stripe.com/c/pay/cs_setup_intent_${data.setup_intent_id}`;
                  
                  // For now, use a simple approach - open Stripe's hosted setup page
                  // In production, you'd use Stripe Elements
                  toast.success("Redirecting to secure payment setup...");
                  
                  // Create a setup checkout session instead
                  const setupRes = await fetch(`${API}/stripe/setup-checkout`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ 
                      success_url: `${window.location.origin}/merchant?payment_setup=success`,
                      cancel_url: `${window.location.origin}/merchant?payment_setup=cancel`
                    })
                  });
                  
                  if (setupRes.ok) {
                    const setupData = await setupRes.json();
                    if (setupData.url) {
                      window.location.href = setupData.url;
                    }
                  } else {
                    // Fallback: just confirm the setup intent was created
                    toast.success("Payment setup initiated. Please check your email for next steps.");
                    setShowPaymentSetupModal(false);
                    fetchAllData();
                  }
                } catch (error) {
                  console.error("Payment setup error:", error);
                  toast.error("Failed to setup payment. Please try again.");
                } finally {
                  setSettingUpPayment(false);
                }
              }}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              disabled={settingUpPayment}
              data-testid="confirm-payment-setup-btn"
            >
              {settingUpPayment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  {paymentStatus?.has_payment_method ? "Update Card" : "Add Payment Method"}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Detail Modal */}
      <Dialog open={showOrderDetailModal} onOpenChange={setShowOrderDetailModal}>
        <DialogContent className="max-w-lg" data-testid="order-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-indigo-600" />
              Order Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrderForDetail && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs text-slate-500">ORDER ID</p>
                    <p className="font-mono text-indigo-600">{selectedOrderForDetail.order_id}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    selectedOrderForDetail.status === "paid" ? "bg-emerald-100 text-emerald-700" :
                    selectedOrderForDetail.status === "refunded" ? "bg-red-100 text-red-700" :
                    "bg-amber-100 text-amber-700"
                  }`}>
                    {selectedOrderForDetail.status}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  {selectedOrderForDetail.created_at && new Date(selectedOrderForDetail.created_at).toLocaleString()}
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Customer</h4>
                <p className="text-sm">{selectedOrderForDetail.customer_name || "Customer"}</p>
                <p className="text-sm text-slate-500">{selectedOrderForDetail.customer_email || "-"}</p>
              </div>
              
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Items</h4>
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  {selectedOrderForDetail.items ? (
                    selectedOrderForDetail.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>{item.name} x {item.quantity}</span>
                        <span className="font-medium">£{(item.total || item.unit_price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-between text-sm">
                      <span>{selectedOrderForDetail.product_name || "Product"} x {selectedOrderForDetail.quantity || 1}</span>
                      <span className="font-medium">£{selectedOrderForDetail.total_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                    <span>Total</span>
                    <span className="text-indigo-600">£{selectedOrderForDetail.total_amount?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => handleSendReceipt(selectedOrderForDetail.order_id)}
                  className="flex-1"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Receipt
                </Button>
                {selectedOrderForDetail.status === "paid" && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedOrderForRefund(selectedOrderForDetail);
                      setShowOrderDetailModal(false);
                      setShowRefundModal(true);
                    }}
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Refund Order
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <Dialog open={showRefundModal} onOpenChange={setShowRefundModal}>
        <DialogContent className="max-w-md" data-testid="refund-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Process Refund
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The customer will be refunded via Stripe.
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrderForRefund && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">Order: <span className="font-mono text-indigo-600">{selectedOrderForRefund.order_id}</span></p>
                <p className="text-sm text-slate-600">Amount: <span className="font-semibold">£{selectedOrderForRefund.total_amount?.toFixed(2)}</span></p>
              </div>
              
              <div>
                <Label>Refund Amount (leave empty for full refund)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={`Full refund: £${selectedOrderForRefund.total_amount?.toFixed(2)}`}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                />
              </div>
              
              <div>
                <Label>Refund Reason</Label>
                <Select value={refundReason} onValueChange={setRefundReason}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="requested_by_customer">Requested by customer</SelectItem>
                    <SelectItem value="duplicate">Duplicate charge</SelectItem>
                    <SelectItem value="fraudulent">Fraudulent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRefundModal(false);
                setSelectedOrderForRefund(null);
                setRefundAmount("");
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRefundOrder}
              disabled={processingRefund}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {processingRefund ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                "Confirm Refund"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerchantDashboard;
